import {ensureAmplify} from './amplify-bootstrap.js';
import {translateCognitoError} from './auth-errors.js';
import {userSignal, pendingChallengeSignal} from './auth.store.js';
import {SESSION_KEY, TOKEN_STORAGE_KEYS} from './auth.types.js';
import type {AuthUser, LoginResult, PasswordPolicyResult, PasswordRule} from './auth.types.js';

function persistSession(user: AuthUser, idToken?: string, accessToken?: string): void {
    try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(user));
        if (idToken) localStorage.setItem('idToken', idToken);
        if (accessToken) localStorage.setItem('accessToken', accessToken);
        if (user.userId) localStorage.setItem('userId', user.userId);
        if (user.email) localStorage.setItem('userEmail', user.email);
    } catch {}
}

/** Limpia las keys propias que Amplify/Cognito deja en localStorage
 *  (CognitoIdentityServiceProvider.*, aws-amplify.*) para que no quede un
 *  "current user" colgado (ej. tras una sesión expirada). signOut() de Amplify
 *  debería hacerlo, pero depende de red/latencia; esto es garantía local. */
function clearAmplifyStorage(): void {
    try {
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && (k.startsWith('CognitoIdentityServiceProvider') || k.startsWith('aws-amplify'))) {
                toRemove.push(k);
            }
        }
        for (const k of toRemove) localStorage.removeItem(k);
    } catch {}
}

function clearStorage(): void {
    try {
        localStorage.removeItem(SESSION_KEY);
        for (const key of TOKEN_STORAGE_KEYS) localStorage.removeItem(key);
        clearAmplifyStorage();
    } catch {}
}

/** ¿El error de Amplify indica que ya hay un usuario firmado (stale)? */
function isAlreadySignedInError(error: unknown): boolean {
    const err = error as {name?: string; code?: string; message?: string};
    const code = err?.name ?? err?.code ?? '';
    const msg = (err?.message ?? '').toLowerCase();
    return code === 'UserAlreadyAuthenticatedException' || (msg.includes('already') && msg.includes('signed in'));
}

async function handleSignInSuccess(): Promise<void> {
    const {fetchAuthSession, fetchUserAttributes} = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const tokens = session.tokens;
    if (!tokens?.idToken) throw new Error('No idToken in session');

    const idToken = tokens.idToken.toString();
    const accessToken = tokens.accessToken?.toString() ?? '';

    let attrs: Record<string, string> = {};
    try {
        attrs = (await fetchUserAttributes()) as Record<string, string>;
    } catch {}

    const user: AuthUser = {
        email: attrs['email'] ?? '',
        name: attrs['name'] ?? attrs['email'] ?? '',
        userId: attrs['sub'] ?? '',
    };

    userSignal.value = user;
    persistSession(user, idToken, accessToken);
}

export const authService = {
    get user(): AuthUser | null {
        return userSignal.value;
    },
    get isLoggedIn(): boolean {
        return userSignal.value !== null;
    },

    restoreSession(): void {
        try {
            const raw = localStorage.getItem(SESSION_KEY);
            if (!raw) return;
            const data = JSON.parse(raw) as Partial<AuthUser>;
            if (data && data.email) {
                userSignal.value = {
                    email: data.email,
                    name: data.name ?? data.email,
                    userId: data.userId ?? '',
                };
            }
        } catch {}
    },

    async login(email: string, password: string): Promise<LoginResult> {
        const username = email.toLowerCase().trim();
        try {
            await ensureAmplify();
            const {signIn} = await import('aws-amplify/auth');

            let result;
            try {
                result = await signIn({username, password});
            } catch (signInError) {
                // Amplify puede dejar un "current user" colgado (ej. tras sesión
                // expirada) → signIn falla con "There is already a signed in user".
                // Limpiar stale y reintentar una sola vez.
                if (!isAlreadySignedInError(signInError)) throw signInError;
                try {
                    const {signOut} = await import('aws-amplify/auth');
                    await signOut();
                } catch {}
                clearStorage();
                result = await signIn({username, password});
            }

            return await this._handleSignInResult(result);
        } catch (error) {
            console.error('[clam-auth] login() error:', error);
            return {success: false, message: translateCognitoError(error)};
        }
    },

    async _handleSignInResult(result: Awaited<ReturnType<typeof import('aws-amplify/auth')['signIn']>>): Promise<LoginResult> {
        if (result.isSignedIn) {
            await handleSignInSuccess();
            return {success: true};
        }

        if (result.nextStep) {
            const step = result.nextStep;
            switch (step.signInStep) {
                case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
                case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE':
                    pendingChallengeSignal.value = {
                        challengeName: step.signInStep,
                        username: '',
                    };
                    return {success: false, prompt: 'mfaCode', challengeName: step.signInStep};

                case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                    pendingChallengeSignal.value = {
                        challengeName: 'NEW_PASSWORD_REQUIRED',
                        username: '',
                        missingAttributes: step.missingAttributes ?? [],
                    };
                    return {success: false, prompt: 'newPassword'};

                case 'RESET_PASSWORD':
                    return {
                        success: false,
                        prompt: 'forgotPassword',
                        message: 'Tu contrasena ha expirado. Debes restablecerla.',
                    };

                case 'DONE':
                    await handleSignInSuccess();
                    return {success: true};

                default:
                    return {success: false, message: `Paso no soportado: ${step.signInStep}`};
            }
        }

        return {success: false, message: 'Respuesta inesperada del servidor'};
    },

    async verifyMFACode(code: string): Promise<LoginResult> {
        if (!pendingChallengeSignal.value) {
            return {success: false, message: 'No hay challenge pendiente'};
        }
        try {
            await ensureAmplify();
            const {confirmSignIn} = await import('aws-amplify/auth');
            const result = await confirmSignIn({challengeResponse: code});
            if (result.isSignedIn) {
                pendingChallengeSignal.value = null;
                await handleSignInSuccess();
                return {success: true};
            }
            return {success: false, message: 'Codigo incorrecto'};
        } catch (error) {
            return {success: false, message: translateCognitoError(error)};
        }
    },

    async confirmNewPassword(
        newPassword: string,
        userAttributes?: Record<string, string>,
    ): Promise<LoginResult> {
        if (!pendingChallengeSignal.value) {
            return {success: false, message: 'No hay challenge pendiente'};
        }
        try {
            await ensureAmplify();
            const {confirmSignIn} = await import('aws-amplify/auth');
            // Amplify v6: `options.userAttributes` se pasa al challenge NEW_PASSWORD_REQUIRED
            // como `userAttributes.<key>` en ChallengeResponses. Sí, está indocumentado en la
            // API pública pero el código interno (handleCompleteNewPasswordChallenge) lo usa.
            const result = userAttributes && Object.keys(userAttributes).length > 0
                ? await confirmSignIn({challengeResponse: newPassword, options: {userAttributes}})
                : await confirmSignIn({challengeResponse: newPassword});
            if (result.isSignedIn) {
                pendingChallengeSignal.value = null;
                await handleSignInSuccess();
                return {success: true};
            }
            return {success: false, message: 'No se pudo cambiar la contrasena'};
        } catch (error) {
            return {success: false, message: translateCognitoError(error)};
        }
    },

    async forgotPassword(username: string): Promise<LoginResult> {
        try {
            await ensureAmplify();
            const {resetPassword} = await import('aws-amplify/auth');
            await resetPassword({username: username.toLowerCase().trim()});
            return {success: true, prompt: 'confirmReset'};
        } catch (error) {
            return {success: false, message: translateCognitoError(error)};
        }
    },

    async confirmForgotPassword(username: string, code: string, newPassword: string): Promise<LoginResult> {
        try {
            await ensureAmplify();
            const {confirmResetPassword} = await import('aws-amplify/auth');
            await confirmResetPassword({
                username: username.toLowerCase().trim(),
                confirmationCode: code,
                newPassword,
            });
            return {success: true};
        } catch (error) {
            return {success: false, message: translateCognitoError(error)};
        }
    },

    async checkSession(): Promise<boolean> {
        try {
            await ensureAmplify();
            const {fetchAuthSession, fetchUserAttributes} = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            if (session.tokens?.idToken) {
                const attrs = (await fetchUserAttributes()) as Record<string, string>;
                const user: AuthUser = {
                    email: attrs['email'] ?? '',
                    name: attrs['name'] ?? attrs['email'] ?? '',
                    userId: attrs['sub'] ?? '',
                };
                userSignal.value = user;
                persistSession(user, session.tokens.idToken.toString());
                return true;
            }
            userSignal.value = null;
            clearStorage();
            return false;
        } catch {
            userSignal.value = null;
            clearStorage();
            return false;
        }
    },

    async refreshSession(): Promise<boolean> {
        try {
            await ensureAmplify();
            const {fetchAuthSession} = await import('aws-amplify/auth');
            const session = await fetchAuthSession({forceRefresh: true});
            return !!session.tokens?.idToken;
        } catch {
            return false;
        }
    },

    async logout(): Promise<void> {
        try {
            await ensureAmplify();
            const {signOut} = await import('aws-amplify/auth');
            try {
                await signOut({global: true});
            } catch {}
            try {
                await signOut();
            } catch {}
        } catch {}
        userSignal.value = null;
        pendingChallengeSignal.value = null;
        clearStorage();
    },

    validatePasswordPolicy(password: string): PasswordPolicyResult {
        const rules: PasswordRule[] = [
            {label: 'Minimo 16 caracteres', passed: password.length >= 16},
            {label: 'Al menos 1 mayuscula', passed: /[A-Z]/.test(password)},
            {label: 'Al menos 1 minuscula', passed: /[a-z]/.test(password)},
            {label: 'Al menos 1 numero', passed: /\d/.test(password)},
            {label: 'Al menos 1 simbolo', passed: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)},
        ];
        return {valid: rules.every((r) => r.passed), rules};
    },
};
